import { Star, ArrowRight } from "lucide-react";
import { TESTIMONIALS } from "@/lib/data";
import { IOSHighlight } from "@/components/site/iOSKit";

// Pill component — capsule shaped testimonial
const Pill = ({ children, variant = "dark", className = "", style, testid }) => {
    const styles = {
        dark: "bg-[#141414] border border-white/10 text-white",
        red: "bg-[#FF2A2A] text-white",
        "red-soft": "bg-[#FF2A2A]/15 border border-[#FF2A2A]/30 text-white",
        light: "bg-[#F3F4F6] text-[#0A0A0A]",
        outline: "bg-transparent border border-white/20 text-white",
    };
    return (
        <div
            data-testid={testid}
            className={`rounded-full px-6 py-4 md:px-8 md:py-5 ${styles[variant]} ${className}`}
            style={style}
        >
            {children}
        </div>
    );
};

// Avatar — circular headshot
const Avatar = ({ label, img, size = 120, className = "", style, testid }) => {
    return (
        <div
            data-testid={testid}
            className={`rounded-full overflow-hidden bg-[#FF2A2A] flex items-center justify-center border-2 border-[#0A0A0A] shadow-[0_10px_30px_-6px_rgba(0,0,0,0.6)] ${className}`}
            style={{ width: size, height: size, ...style }}
        >
            {img ? (
                <img
                    src={img}
                    alt={label}
                    className="w-full h-full object-cover"
                />
            ) : (
                <span className="font-display text-white text-3xl">
                    {label}
                </span>
            )}
        </div>
    );
};

// Unsplash portrait avatars (free, no attribution required)
const AVATARS = [
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&auto=format&fit=crop&q=80",
];

export const Testimonials = () => {
    const t = TESTIMONIALS;

    return (
        <section
            id="testimonios"
            data-testid="testimonials-section"
            className="relative bg-[#0A0A0A] border-t border-white/5 py-24 md:py-32 px-6 md:px-12 lg:px-20 overflow-hidden"
        >
            {/* grain overlay */}
            <div className="grain" />

            {/* Heading */}
            <div className="relative z-10 max-w-4xl mb-16 md:mb-20">
                <div className="flex items-center gap-3 mb-5">
                    <span className="w-10 h-px bg-[#FF2A2A]" />
                    <span className="text-[11px] tracking-[0.3em] uppercase text-white/60">
                        Opiniones · 4.9 / 5.0 · +100 de clientes felices
                    </span>
                </div>
                <h2
                    data-testid="testimonials-heading"
                    className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-balance"
                >
                    Lo que <span className="font-serif text-white/90">dicen</span>{" "}
                    <IOSHighlight variant="red">quienes</IOSHighlight> ya
                    trabajaron con nosotros.
                </h2>
            </div>

            {/* Collage canvas - pills + avatars scattered (compact) */}
            <div
                data-testid="testimonials-collage"
                className="relative min-h-[1080px] md:min-h-[640px] lg:min-h-[560px]"
            >
                {/* ROW 1 — María */}
                <Pill
                    variant="dark"
                    className="absolute top-0 left-0 md:top-2 md:left-8 max-w-[92%] md:max-w-[580px] animate-fade-up"
                    style={{ animationDelay: "0.05s" }}
                    testid="pill-t1"
                >
                    <div className="flex items-center gap-4 md:gap-5">
                        <span className="font-display text-lg md:text-xl leading-snug flex-1">
                            “Subieron nuestro CTR un{" "}
                            <span className="text-[#FF2A2A]">340%</span>. El
                            mejor spot que hemos sacado en 5 años.”
                        </span>
                    </div>
                </Pill>

                <Avatar
                    img={AVATARS[0]}
                    size={110}
                    className="absolute top-[70px] md:top-0 right-4 md:right-32 animate-fade-up"
                    style={{ animationDelay: "0.15s" }}
                    testid="avatar-t1"
                />

                <Pill
                    variant="red-soft"
                    className="absolute top-[200px] md:top-[90px] left-8 md:left-[220px] animate-fade-up"
                    style={{ animationDelay: "0.25s" }}
                    testid="pill-t1-meta"
                >
                    <div className="flex items-center gap-3 text-xs md:text-sm">
                        <div className="flex gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                    key={i}
                                    className="w-3 h-3 fill-[#FF2A2A] text-[#FF2A2A]"
                                />
                            ))}
                        </div>
                        <span className="text-white/80">
                            — María González · Nova Retail
                        </span>
                    </div>
                </Pill>

                {/* ROW 2 — Diego */}
                <Pill
                    variant="light"
                    className="absolute top-[280px] md:top-[170px] right-0 md:right-8 max-w-[92%] md:max-w-[520px] animate-fade-up"
                    style={{ animationDelay: "0.35s" }}
                    testid="pill-t2"
                >
                    <div className="flex items-center gap-4">
                        <span className="font-display text-lg md:text-xl leading-snug">
                            “Pedimos branding, nos entregaron una{" "}
                            <span className="text-[#FF2A2A]">identidad</span>{" "}
                            que los clientes reconocen a dos cuadras.”
                        </span>
                    </div>
                </Pill>

                <Avatar
                    img={AVATARS[2]}
                    size={90}
                    className="absolute top-[420px] md:top-[270px] right-[180px] md:right-[550px] animate-fade-up"
                    style={{ animationDelay: "0.45s" }}
                    testid="avatar-t2"
                />

                <Pill
                    variant="outline"
                    className="absolute top-[490px] md:top-[290px] right-8 md:right-16 animate-fade-up"
                    style={{ animationDelay: "0.5s" }}
                    testid="pill-t2-meta"
                >
                    <span className="text-xs md:text-sm text-white/70">
                        — Diego Fuentes ·{" "}
                        <span className="text-white">Meridian Coffee</span>
                    </span>
                </Pill>

                {/* ROW 3 — Camila */}
                <Avatar
                    img={AVATARS[1]}
                    size={80}
                    className="absolute top-[570px] md:top-[360px] left-0 md:left-12 animate-fade-up"
                    style={{ animationDelay: "0.55s" }}
                    testid="avatar-t3"
                />

                <Pill
                    variant="red"
                    className="absolute top-[670px] md:top-[370px] left-8 md:left-[140px] max-w-[92%] md:max-w-[560px] animate-fade-up"
                    style={{ animationDelay: "0.6s" }}
                    testid="pill-t3"
                >
                    <span className="font-display text-lg md:text-xl leading-snug">
                        “Rápidos, brutales con el concepto. Defendieron la idea
                        — y tuvieron razón.”
                    </span>
                </Pill>

                <Pill
                    variant="dark"
                    className="absolute top-[820px] md:top-[470px] left-8 md:left-[170px] animate-fade-up"
                    style={{ animationDelay: "0.7s" }}
                    testid="pill-t3-meta"
                >
                    <div className="flex items-center gap-3 text-xs md:text-sm">
                        <Avatar
                            img={AVATARS[3]}
                            size={24}
                            className="!border-0 !shadow-none"
                        />
                        <span className="text-white/80">
                            Camila Ruiz · Creative Director, Atlas Studio
                        </span>
                        <div className="flex gap-0.5 ml-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                    key={i}
                                    className="w-3 h-3 fill-[#FF2A2A] text-[#FF2A2A]"
                                />
                            ))}
                        </div>
                    </div>
                </Pill>

                {/* ROW 4 — Tomás (big pill) */}
                <Pill
                    variant="dark"
                    className="absolute top-[920px] md:top-[480px] right-0 md:right-20 max-w-[92%] md:max-w-[540px] animate-fade-up"
                    style={{ animationDelay: "0.8s" }}
                    testid="pill-t4"
                >
                    <div className="flex items-center gap-4">
                        <Avatar
                            label="T"
                            size={42}
                            className="!border-0 !shadow-none shrink-0"
                        />
                        <span className="font-display text-base md:text-lg leading-snug">
                            “Rompimos el{" "}
                            <span className="text-[#FF2A2A]">ROAS</span>{" "}
                            objetivo en el primer mes. Lo que prometen, lo
                            ejecutan.”
                        </span>
                    </div>
                </Pill>

                {/* Floating decorative tags */}
                <Pill
                    variant="outline"
                    className="hidden md:block absolute top-[150px] left-[620px] text-xs animate-fade-up"
                    style={{ animationDelay: "0.9s" }}
                >
                    <span className="text-white/50">Spot · 0:30</span>
                </Pill>

                <Pill
                    variant="outline"
                    className="hidden md:block absolute top-[420px] left-[40px] text-xs animate-fade-up"
                    style={{ animationDelay: "0.95s" }}
                >
                    <span className="text-white/50">Branding · Q3</span>
                </Pill>
            </div>

            {/* ¿Por qué elegirnos? — phrase left + stats right */}
            <div className="relative z-10 mt-16 md:mt-20 pt-14 border-t border-white/10">
                <div className="flex items-start justify-between gap-6 flex-wrap mb-10 md:mb-14">
                    <div className="flex items-center gap-3">
                        <span className="w-10 h-px bg-[#FF2A2A]" />
                        <span className="text-[11px] tracking-[0.3em] uppercase text-white/60">
                            Números que hablan
                        </span>
                    </div>
                    <div className="text-right">
                        <div className="text-[11px] tracking-[0.3em] uppercase text-white/60 leading-tight">
                            Creamos lo
                            <br />
                            extraordinario
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    {/* Left: headline phrase */}
                    <div
                        data-testid="why-choose-us-heading"
                        className="lg:col-span-6 font-display leading-[0.85] tracking-tighter text-[14vw] md:text-[9vw] lg:text-[6.5vw] text-white uppercase"
                    >
                        <div>NÚMEROS</div>
                        <div>
                            QUE{" "}
                            <span className="font-serif text-white/60 normal-case lowercase">
                                hablan
                            </span>
                        </div>
                        <div className="flex items-center gap-3 md:gap-5">
                            <span className="inline-flex items-center justify-center w-[1em] h-[1em] rounded-full bg-[#FF2A2A] shrink-0">
                                <ArrowRight
                                    className="w-[0.45em] h-[0.45em] text-white"
                                    strokeWidth={2.5}
                                />
                            </span>
                            <span
                                className="text-transparent"
                                style={{
                                    WebkitTextStroke: "1.5px rgba(255,255,255,0.4)",
                                }}
                            >
                                POR NOSOTROS
                            </span>
                        </div>
                    </div>

                    {/* Right: stat cards */}
                    <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                        <div
                            data-testid="stat-card-experience"
                            className="sm:col-span-2 bg-white text-[#0A0A0A] rounded-[26px] px-8 py-10 flex flex-col justify-center min-h-[160px]"
                        >
                            <div className="font-display text-6xl md:text-7xl leading-none tracking-tighter">
                                +15
                            </div>
                            <div className="mt-3 text-sm md:text-base text-[#0A0A0A]/70">
                                años de experiencia en el rubro
                            </div>
                        </div>

                        <div
                            data-testid="stat-card-worldwide"
                            className="bg-[#FF2A2A] text-white rounded-[26px] px-8 py-10 flex flex-col justify-between min-h-[180px]"
                        >
                            <div className="font-display text-5xl md:text-6xl leading-none tracking-tighter">
                                +15
                            </div>
                            <div className="mt-4 text-sm md:text-base text-white/90">
                                clientes fijos en servicios publicitarios
                            </div>
                        </div>

                        <div
                            data-testid="stat-card-clients"
                            className="bg-white text-[#0A0A0A] rounded-[26px] px-8 py-10 flex flex-col justify-center min-h-[180px]"
                        >
                            <div className="font-display text-5xl md:text-6xl leading-none tracking-tighter">
                                +50
                            </div>
                            <div className="mt-3 text-sm md:text-base text-[#0A0A0A]/70">
                                marcas confían en nosotros
                            </div>
                        </div>

                        <div
                            data-testid="stat-card-projects"
                            className="bg-white text-[#0A0A0A] rounded-[26px] px-8 py-10 flex flex-col justify-center min-h-[160px]"
                        >
                            <div className="font-display text-5xl md:text-6xl leading-none tracking-tighter">
                                +500
                            </div>
                            <div className="mt-3 text-sm md:text-base text-[#0A0A0A]/70">
                                proyectos entregados
                            </div>
                        </div>

                        <div
                            data-testid="stat-card-rating"
                            className="bg-[#141414] border border-white/10 text-white rounded-[26px] px-8 py-10 flex flex-col justify-center min-h-[160px]"
                        >
                            <div className="font-display text-5xl md:text-6xl leading-none tracking-tighter">
                                4.9
                                <span className="text-[#FF2A2A]">/5</span>
                            </div>
                            <div className="mt-3 text-sm md:text-base text-white/60">
                                calificación promedio
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
